import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/contexts/CartContext";
import Index from "./pages/Index";

const queryClient = new QueryClient();

// Lazy-loaded routes
const PCSurMesure = lazy(() => import("./pages/PCSurMesure"));
const CreationWeb = lazy(() => import("./pages/CreationWeb"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Appointments = lazy(() => import("./pages/Appointments"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Install = lazy(() => import("./pages/Install"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ZonesIntervention = lazy(() => import("./pages/ZonesIntervention"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Shop = lazy(() => import("./pages/Shop"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Configurateur = lazy(() => import("./pages/Configurateur"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const PannePage = lazy(() => import("./pages/PannePage"));
const PannesIndex = lazy(() => import("./pages/PannePage").then((m) => ({ default: m.PannesIndex })));
const Tarifs = lazy(() => import("./pages/InfoPages").then((m) => ({ default: m.Tarifs })));
const Realisations = lazy(() => import("./pages/InfoPages").then((m) => ({ default: m.Realisations })));
const Avis = lazy(() => import("./pages/InfoPages").then((m) => ({ default: m.Avis })));
const APropos = lazy(() => import("./pages/InfoPages").then((m) => ({ default: m.APropos })));
const MentionsLegales = lazy(() => import("./pages/InfoPages").then((m) => ({ default: m.MentionsLegales })));
const Confidentialite = lazy(() => import("./pages/InfoPages").then((m) => ({ default: m.Confidentialite })));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const QuartierPage = lazy(() => import("./pages/QuartierPage"));
const AnnuaireBeziers = lazy(() => import("./pages/AnnuaireBeziers"));

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminProjects = lazy(() => import("./pages/admin/AdminProjects"));
const AdminAppointments = lazy(() => import("./pages/admin/AdminAppointments"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminFAQ = lazy(() => import("./pages/admin/AdminFAQ"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminChatPage = lazy(() => import("./pages/admin/AdminChatPage"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminDiagnostics = lazy(() => import("./pages/admin/AdminDiagnostics"));
const AdminSeoCheck = lazy(() => import("./pages/admin/AdminSeoCheck"));
const AdminSeoIndex = lazy(() => import("./pages/admin/AdminSeoIndex"));

const LiveChat = lazy(() => import("./components/LiveChatDeferred"));
const InstallBanner = lazy(() => import("./components/InstallBanner"));
const GoogleMerchantBadge = lazy(() => import("./components/GoogleMerchantBadge"));
const MobileBottomNav = lazy(() => import("./components/MobileBottomNav"));

const NonCriticalWidgets = () => {
  const location = useLocation();
  const isCommerceRoute = ["/boutique", "/panier", "/commande"].some((path) => location.pathname.startsWith(path));
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <Suspense fallback={null}>
      <InstallBanner />
      <LiveChat />
      {!isAdminRoute && <MobileBottomNav />}
      {isCommerceRoute && <GoogleMerchantBadge />}
    </Suspense>
  );
};

const App = () => {
  const [showNonCriticalWidgets, setShowNonCriticalWidgets] = useState(false);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => setShowNonCriticalWidgets(true), 1200);
    return () => globalThis.clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <CartProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={null}>
                <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/boutique" element={<Shop />} />
                <Route path="/configurateur" element={<Configurateur />} />
                <Route path="/panier" element={<Cart />} />
                <Route path="/commande" element={<Checkout />} />
                <Route path="/confirmation" element={<OrderConfirmation />} />
                <Route path="/pc-sur-mesure" element={<PCSurMesure />} />
                <Route path="/creation-web" element={<CreationWeb />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/rdv" element={<Appointments />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/install" element={<Install />} />
                <Route path="/depannage-informatique" element={<ServicePage slug="depannage" />} />
                <Route path="/conseil-informatique" element={<ServicePage slug="conseil" />} />
                <Route path="/assistance-bureautique" element={<ServicePage slug="bureautique" />} />
                <Route path="/pannes" element={<PannesIndex />} />
                <Route path="/pannes/:slug" element={<PannePage />} />
                <Route path="/tarifs" element={<Tarifs />} />
                <Route path="/realisations" element={<Realisations />} />
                <Route path="/avis" element={<Avis />} />
                <Route path="/a-propos" element={<APropos />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/confidentialite" element={<Confidentialite />} />
                <Route path="/depannage-informatique-:city" element={<Navigate to="/zones-intervention" replace />} />
                <Route path="/zones-intervention" element={<ZonesIntervention />} />
                <Route path="/services/:serviceCity" element={<Navigate to="/zones-intervention" replace />} />
                <Route path="/comparatif-:variant" element={<Navigate to="/zones-intervention" replace />} />
                <Route path="/alternative-:variant" element={<Navigate to="/zones-intervention" replace />} />
                <Route path="/quartier-beziers-:slug" element={<QuartierPage />} />
                <Route path="/annuaire-informatique-beziers" element={<AnnuaireBeziers />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/plan-du-site" element={<Sitemap />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="clients" element={<AdminClients />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route path="appointments" element={<AdminAppointments />} />
                  <Route path="quotes" element={<AdminQuotes />} />
                  <Route path="invoices" element={<AdminInvoices />} />
                  <Route path="messages" element={<AdminMessages />} />
                  <Route path="demandes" element={<AdminLeads />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="faq" element={<AdminFAQ />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="logs" element={<AdminLogs />} />
                  <Route path="chat" element={<AdminChatPage />} />
                  <Route path="security" element={<AdminSecurity />} />
                  <Route path="diagnostics" element={<AdminDiagnostics />} />
                  <Route path="seo-check" element={<AdminSeoCheck />} />
                  <Route path="seo-index" element={<AdminSeoIndex />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              {showNonCriticalWidgets && (
                <NonCriticalWidgets />
              )}
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
